import React, {
  PropsWithChildren,
  useContext,
  useCallback,
  ReactNode,
} from 'react';

import { Map as LeafletMap, LeafletConsumer } from 'react-leaflet';
import clsx from 'clsx';

import { MapTileLayers } from '@atoms/map-tile-layers';

import {
  MapContext,
  maxZoom,
  minZoom,
  maxBounds,
} from '@contexts/map-context/map-context';

import { MIN_ZOOM_LEVEL_FOR_CURSOR_WITH_TEXT } from '@atlas/molecules/map-cursor-with-text';

import { useGaEvents, useOnClickDetailOpen } from './map.hooks';

import classes from './map.module.scss';

type MapProps = {
  overlay: ReactNode;
};

const Map = ({ children, overlay }: PropsWithChildren<MapProps>) => {
  const { satellite, initialize, zoom } = useContext(MapContext);

  // provide leaflet context to the map context
  const leafletConsumerCallback = useCallback(
    (leafletContext) => {
      initialize(leafletContext);
      return null;
    },
    [initialize],
  );

  // ga events
  useGaEvents();

  const { onMapClick, onMapDoubleClick } = useOnClickDetailOpen(zoom);

  return (
    <div className={classes.map}>
      <LeafletMap
        className={clsx(
          classes.leafletMap,
          zoom >= MIN_ZOOM_LEVEL_FOR_CURSOR_WITH_TEXT && classes.pointer,
        )}
        maxZoom={maxZoom}
        minZoom={minZoom}
        boundsOptions={{ padding: [10, 10] }}
        maxBounds={maxBounds}
        onClick={onMapClick}
        ondblclick={onMapDoubleClick}
      >
        <LeafletConsumer>{leafletConsumerCallback}</LeafletConsumer>

        <MapTileLayers satellite={satellite} parcels />

        {children}
      </LeafletMap>

      {overlay}
    </div>
  );
};

export default Map;
